import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Euro, 
  Leaf, 
  Battery, 
  TrendingUp, 
  Wrench, 
  Gift,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const Beneficios = () => {
  const benefits = [
    {
      icon: Euro,
      title: 'Ahorro Económico',
      description: 'Reduce tu factura eléctrica hasta un 70% desde el primer mes de instalación.',
      details: [
        'Ahorro inmediato en tu factura',
        'Amortización de la inversión en 1-3 años',
        'Ahorro acumulado de más de 16.500€ en 25 años',
        'Protección contra subidas de tarifas',
        'Posibilidad de vender excedentes'
      ],
      stat: '€1,200',
      statLabel: 'Ahorro anual medio'
    },
    {
      icon: Leaf,
      title: 'Sostenibilidad',
      description: 'Contribuye activamente al cuidado del medio ambiente utilizando energía 100% renovable.',
      details: [
        'Cero emisiones de CO₂',
        'Energía limpia e inagotable',
        'Reducción de la huella de carbono',
        'Contribución a la lucha contra el cambio climático',
        'Futuro sostenible para las próximas generaciones'
      ],
      stat: '100%',
      statLabel: 'Energía renovable'
    },
    {
      icon: Battery,
      title: 'Independencia Energética',
      description: 'Genera tu propia electricidad y reduce tu dependencia de las compañías eléctricas.',
      details: [
        'Autosuficiencia energética',
        'Mayor control sobre tu consumo',
        'Protección frente a cortes de suministro',
        'Almacenamiento con baterías opcionales',
        'Libertad energética'
      ],
      stat: 'Protección',
      statLabel: 'Contra subidas de precio'
    },
    {
      icon: TrendingUp,
      title: 'Rentabilidad',
      description: 'Una inversión que se revaloriza con el tiempo y aumenta el valor de tu propiedad.',
      details: [
        'ROI positivo desde el primer día',
        'Amortización rápida de la inversión',
        'Aumento del valor inmobiliario hasta un 4%',
        'Mayor atractivo para compradores',
        'Mejora de la certificación energética'
      ],
      stat: '+€15,000',
      statLabel: 'Incremento valor vivienda'
    },
    {
      icon: Wrench,
      title: 'Bajo Mantenimiento',
      description: 'Los paneles solares requieren muy poco mantenimiento y tienen una vida útil superior a 25 años.',
      details: [
        'Mantenimiento mínimo requerido',
        'Limpieza ocasional de paneles',
        'Garantía de fabricante de 25 años',
        'Garantía de instalación de 10 años',
        'Soporte técnico permanente'
      ],
      stat: '25 años',
      statLabel: 'Vida útil garantizada'
    },
    {
      icon: Gift,
      title: 'Ayudas y Subvenciones en España',
      description: 'Reduce tu inversión hasta un 40% aprovechando las bonificaciones fiscales y subvenciones disponibles.',
      details: [
        'Deducción fiscal en el IRPF hasta 40%',
        'Bonificaciones municipales del IBI hasta 50%',
        'Bonificaciones del ICIO hasta 95%',
        'Subvenciones autonómicas del 20% al 50%',
        'Incentivos especiales para autoconsumo colectivo'
      ],
      stat: 'Hasta 40%',
      statLabel: 'Reducción de inversión'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-nubia-green-primary mb-6">
              Beneficios de la Energía Solar
            </h1>
            <p className="text-xl text-nubia-text-dark/70 leading-relaxed">
              Descubre todas las ventajas de generar tu propia energía limpia y renovable
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg text-nubia-text-dark/70 leading-relaxed mb-8">
              Instalar paneles solares no es solo una decisión económica inteligente, es una 
              inversión en un futuro más sostenible. Miles de familias y empresas en España ya están 
              disfrutando de los múltiples beneficios de la energía solar.
            </p>
            <p className="text-lg text-nubia-text-dark/70 leading-relaxed">
              A continuación te explicamos en detalle todas las ventajas que obtendrás al unirte 
              a la revolución solar con Nubia Solar.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto space-y-24">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div key={index} className={`grid md:grid-cols-2 gap-12 items-center`}>
                  <div className={isEven ? 'md:order-1' : 'md:order-2'}>
                    <div className="w-16 h-16 bg-nubia-gold-soft/20 rounded-2xl flex items-center justify-center mb-6">
                      <Icon className="w-8 h-8 text-nubia-gold-primary" />
                    </div>
                    <h2 className="text-4xl font-bold text-nubia-green-primary mb-4">
                      {benefit.title}
                    </h2>
                    <p className="text-lg text-nubia-text-dark/70 mb-8 leading-relaxed">
                      {benefit.description}
                    </p>
                    <div className="space-y-3 mb-8">
                      {benefit.details.map((detail, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-nubia-green-support mt-0.5 flex-shrink-0" />
                          <span className="text-nubia-text-dark/70">{detail}</span>
                        </div>
                      ))}
                    </div>
                    <Card className="border-2 border-nubia-gold-primary/30 bg-white inline-block">
                      <CardContent className="pt-6">
                        <div className="text-4xl font-bold text-nubia-gold-primary mb-1">{benefit.stat}</div>
                        <div className="text-sm text-nubia-text-dark/60">{benefit.statLabel}</div>
                      </CardContent>
                    </Card>
                  </div>
                  <div className={isEven ? 'md:order-2' : 'md:order-1'}>
                    <div className="bg-white rounded-2xl p-12 nubia-shadow-lg text-center">
                      <Icon className="w-32 h-32 text-nubia-green-support/20 mx-auto" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Summary Stats */}
      <section className="py-24 nubia-gradient text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
              Beneficios en Números
            </h2>
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-nubia-gold-primary mb-2">70%</div>
                <div className="text-lg">Reducción de Factura</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-nubia-gold-primary mb-2">1-3</div>
                <div className="text-lg">Años Amortización</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-nubia-gold-primary mb-2">25+</div>
                <div className="text-lg">Años de Vida Útil</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-nubia-gold-primary mb-2">0%</div>
                <div className="text-lg">Emisiones CO₂</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-nubia-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-6">
            Empieza a Disfrutar de Estos Beneficios
          </h2>
          <p className="text-xl text-nubia-text-dark/70 mb-10 max-w-3xl mx-auto">
            Calcula tu ahorro personalizado o solicita un presupuesto sin compromiso
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/calculadora-solar">
              <Button size="lg" className="bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold px-10 py-7 text-lg shadow-xl">
                Calcular Mi Ahorro
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contacto">
              <Button size="lg" variant="outline" className="border-2 border-nubia-green-primary text-nubia-green-primary hover:bg-nubia-green-primary hover:text-white px-10 py-7 text-lg">
                Solicitar Presupuesto
              </Button>
            </Link>
          </div>
          
          {/* New CTA for Ayudas */}
          <div className="mt-12 pt-12 border-t-2 border-nubia-gray-light">
            <div className="bg-nubia-gold-soft/10 border-2 border-nubia-gold-primary/30 rounded-2xl p-8 max-w-4xl mx-auto">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Gift className="w-8 h-8 text-nubia-gold-primary" />
                <h3 className="text-2xl font-bold text-nubia-green-primary">
                  ¿Quieres conocer todas las ayudas disponibles?
                </h3>
              </div>
              <p className="text-lg text-nubia-text-dark/70 mb-6">
                Información completa y actualizada sobre deducciones fiscales, bonificaciones municipales y subvenciones autonómicas
              </p>
              <Link to="/ayudas-subvenciones">
                <Button size="lg" variant="outline" className="border-2 border-nubia-green-primary text-nubia-green-primary hover:bg-nubia-green-primary hover:text-white">
                  Ver Ayudas y Subvenciones Detalladas
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Beneficios;